/**
 * Shared Language Constants
 * Centralized language definitions used across the application
 */

// Type definitions
export type LanguageCode = 'en' | 'de' | 'es' | 'pt' | 'cs';

export interface LanguageConfig {
  code: LanguageCode;
  name: string;
  nativeName: string;
  francCode: string;
}

export interface UILanguageConfig {
  code: LanguageCode;
  name: string;
  nativeName: string;
}

export type SupportedLanguages = Record<LanguageCode, LanguageConfig>;
export type UILanguages = Record<LanguageCode, UILanguageConfig>;

/**
 * Supported language configurations
 * Used by both LanguageManager and UIManager
 */
export const SUPPORTED_LANGUAGES: SupportedLanguages = {
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    francCode: 'eng',
  },
  de: {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    francCode: 'deu',
  },
  es: {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    francCode: 'spa',
  },
  pt: {
    code: 'pt',
    name: 'Portuguese',
    nativeName: 'Português',
    francCode: 'por',
  },
  cs: {
    code: 'cs',
    name: 'Czech',
    nativeName: 'Čeština',
    francCode: 'ces',
  },
};

/**
 * Default language code
 */
export const DEFAULT_LANGUAGE: LanguageCode = 'en';

/**
 * Convert ISO 639-1 language code to ISO 639-3 format for Gateway API calls
 * @param languageCode - ISO 639-1 language code (e.g., 'en', 'de')
 * @returns ISO 639-3 language code (e.g., 'eng', 'deu')
 */
export function toIso6393(languageCode: LanguageCode): string {
  const config = SUPPORTED_LANGUAGES[languageCode];
  return config?.francCode ?? 'eng';
}

/**
 * The supported language for an ISO 639-3 code from the gateway (#98 part 4:
 * a stored analysis' language); null when it is not one we support.
 */
export function fromIso6393(code: string): LanguageCode | null {
  const entry = Object.entries(SUPPORTED_LANGUAGES).find(
    ([, config]) => config.francCode === code
  );
  return entry ? (entry[0] as LanguageCode) : null;
}

/**
 * Get UI language info (without the franc code)
 * @returns Object with UI-safe language configurations
 */
export function getUILanguages(): UILanguages {
  const uiLanguages: UILanguages = {} as UILanguages;
  for (const [code, config] of Object.entries(SUPPORTED_LANGUAGES)) {
    uiLanguages[code as LanguageCode] = {
      code: config.code,
      name: config.name,
      nativeName: config.nativeName,
    };
  }
  return uiLanguages;
}
